import { differenceInCalendarWeeks } from "date-fns";

export function relativeTime(iso: string): string {
  const now = new Date();
  const date = new Date(iso);
  const diffMin = Math.floor((now.getTime() - date.getTime()) / 60000);

  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;

  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayMidnight = new Date(todayMidnight.getTime() - 86400000);
  const dateMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const timeStr = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  if (dateMidnight.getTime() === yesterdayMidnight.getTime()) return `Yesterday ${timeStr}`;

  const dayStr = date.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  return `${dayStr} ${timeStr}`;
}

export function toDateKey(value: string): string {
  const d = new Date(value);
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
}

// `cutoffMinutes` moves the day boundary away from local midnight (e.g. 420 = 07:00), so an
// overnight sleep isn't bisected at midnight but stays within the "day" it's associated with.
export function splitMinutesByDay(occurredAt: string, endedAt: string, cutoffMinutes = 0): { date: string; minutes: number }[] {
  const cutoffMs = cutoffMinutes * 60000;
  const end = new Date(new Date(endedAt).getTime() - cutoffMs);
  let cursor = new Date(new Date(occurredAt).getTime() - cutoffMs);
  const segments: { date: string; minutes: number }[] = [];
  while (cursor < end) {
    const dayStart = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate());
    const nextDayStart = new Date(dayStart.getTime() + 86400000);
    const segmentEnd = end < nextDayStart ? end : nextDayStart;
    const minutes = Math.round((segmentEnd.getTime() - cursor.getTime()) / 60000);
    if (minutes > 0) segments.push({ date: toDateKey(cursor.toISOString()), minutes });
    cursor = segmentEnd;
  }
  return segments;
}

export function toSleepDayKey(iso: string, cutoffMinutes = 0): string {
  return toDateKey(new Date(new Date(iso).getTime() - cutoffMinutes * 60000).toISOString());
}

// Like `splitMinutesByDay`, but keeps each segment's position within its day (in minutes
// since the cutoff) instead of just a total, so a sleep period can be drawn as a bar.
export function splitSleepPeriodByDay(
  occurredAt: string,
  endedAt: string,
  cutoffMinutes = 0
): { date: string; startOffsetMin: number; endOffsetMin: number }[] {
  const cutoffMs = cutoffMinutes * 60000;
  const end = new Date(new Date(endedAt).getTime() - cutoffMs);
  let cursor = new Date(new Date(occurredAt).getTime() - cutoffMs);
  const segments: { date: string; startOffsetMin: number; endOffsetMin: number }[] = [];
  while (cursor < end) {
    const dayStart = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate());
    const nextDayStart = new Date(dayStart.getTime() + 86400000);
    const segmentEnd = end < nextDayStart ? end : nextDayStart;
    const startOffsetMin = Math.round((cursor.getTime() - dayStart.getTime()) / 60000);
    const endOffsetMin = Math.round((segmentEnd.getTime() - dayStart.getTime()) / 60000);
    if (endOffsetMin > startOffsetMin) segments.push({ date: toDateKey(cursor.toISOString()), startOffsetMin, endOffsetMin });
    cursor = segmentEnd;
  }
  return segments;
}

export const MIN_CALENDAR_DAYS = 7;
export const MAX_CALENDAR_DAYS = 60; // matches the "Last 2 Months" chart range elsewhere in the Logs tab

// Shared by the week-calendar views: a day column per day from `oldestMs` (the earliest
// event actually loaded) through today, capped so a long history doesn't render hundreds of
// empty columns. Defaults to a week when there's no data yet.
export function buildCalendarDayRange(oldestMs: number | null, nowMs: number, cutoffMinutes = 0): { key: string; date: Date }[] {
  const todayKey = toSleepDayKey(new Date(nowMs).toISOString(), cutoffMinutes);
  const [ty, tm, td] = todayKey.split("-").map(Number);
  const todayStart = new Date(ty, tm - 1, td);
  let dayCount = MIN_CALENDAR_DAYS;
  if (oldestMs !== null) {
    const oldestKey = toSleepDayKey(new Date(oldestMs).toISOString(), cutoffMinutes);
    const [oy, om, od] = oldestKey.split("-").map(Number);
    const spanDays = Math.round((todayStart.getTime() - new Date(oy, om - 1, od).getTime()) / 86400000) + 1;
    dayCount = Math.min(MAX_CALENDAR_DAYS, Math.max(MIN_CALENDAR_DAYS, spanDays));
  }
  return Array.from({ length: dayCount }, (_, i) => {
    const date = new Date(todayStart.getTime() - (dayCount - 1 - i) * 86400000);
    const key = [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-");
    return { key, date };
  });
}

export const DEFAULT_SLEEP_DAY_CUTOFF = "07:00";

export function parseCutoffToMinutes(cutoff: string | undefined): number {
  const [h, m] = (cutoff || DEFAULT_SLEEP_DAY_CUTOFF).split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) {
    const [fh, fm] = DEFAULT_SLEEP_DAY_CUTOFF.split(":").map(Number);
    return fh * 60 + fm;
  }
  return h * 60 + m;
}

export function localDatetimeString(d = new Date()): string {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}

export function calculateAgeWeeks(birthDate: string, now = new Date()): number {
  const weeks = differenceInCalendarWeeks(now, new Date(birthDate), {
    weekStartsOn: 1
  });
  return Math.max(0, weeks);
}

export function formatBabyAge(birthDate: string, now = new Date()): string {
  const totalDays = Math.max(0, Math.floor((now.getTime() - new Date(birthDate).getTime()) / 86_400_000));
  if (totalDays < 7) return `${totalDays} day${totalDays !== 1 ? "s" : ""}`;
  const weeks = Math.floor(totalDays / 7);
  const days = totalDays % 7;
  if (weeks < 13) return days > 0 ? `${weeks}w ${days}d` : `${weeks} weeks`;
  const months = Math.floor(totalDays / 30.44);
  if (months < 24) return `${months} months`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem > 0 ? `${years}y ${rem}m` : `${years} years`;
}
